import imageCompression from 'browser-image-compression'

/**
 * Preparación de imágenes en el cliente ANTES de subirlas.
 *
 * Contexto (goal fix subida de imágenes en noticias): las imágenes del CMS del
 * landing se suben por POST multipart al portal-server, pero el proxy/ingress
 * que lo fronta corta el cuerpo en ~1 MB (`client_max_body_size` por defecto de
 * nginx). Una imagen de noticia de varios MB chocaba con ese límite y la subida
 * se quedaba colgada. Aquí recomprimimos/redimensionamos en el navegador para
 * caber bajo ese límite y validamos con un mensaje claro lo que no se puede
 * subir. El manejo del error (toast/nota + reset del spinner) lo hace el llamador.
 */

/**
 * Tipos ráster que SÍ podemos recomprimir/redimensionar en canvas sin perder
 * información esencial. SVG es vector y GIF puede ser animado: se dejan intactos
 * y solo se validan por tamaño.
 */
const COMPRESSIBLE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

const MB = 1024 * 1024

export interface PrepareImageOptions {
  /**
   * Tamaño objetivo tras comprimir (MB). Debe quedar por debajo del límite del
   * proxy (nginx `client_max_body_size`, 1 MB por defecto).
   */
  maxSizeMB?: number
  /** Lado máximo (px) al redimensionar. */
  maxWidthOrHeight?: number
  /**
   * Límite duro: si tras preparar el archivo sigue por encima, se rechaza con un
   * mensaje apto para el usuario. Aplica también a los tipos no comprimibles.
   */
  hardMaxBytes?: number
}

const DEFAULTS: Required<PrepareImageOptions> = {
  maxSizeMB: 0.9,
  maxWidthOrHeight: 1920,
  hardMaxBytes: Math.round(0.95 * MB),
}

export function isImage(file: File): boolean {
  return file.type.startsWith('image/')
}

export function isCompressibleImage(file: File): boolean {
  return COMPRESSIBLE_TYPES.has(file.type)
}

export function formatBytes(bytes: number): string {
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/**
 * Prepara una imagen para subirla: valida que sea imagen y, si es un ráster
 * grande (JPG/PNG/WEBP), la recomprime y redimensiona en el navegador para que
 * quepa bajo el límite del proxy. Los formatos no comprimibles (SVG/GIF) se
 * dejan intactos y solo se validan por tamaño.
 *
 * Lanza un `Error` con mensaje apto para el usuario si el archivo no sirve; el
 * llamador lo muestra (toast/nota) y resetea su estado de carga.
 */
export async function prepareImageForUpload(
  file: File,
  options: PrepareImageOptions = {},
): Promise<File> {
  const { maxSizeMB, maxWidthOrHeight, hardMaxBytes } = {
    ...DEFAULTS,
    ...options,
  }

  if (!isImage(file)) {
    throw new Error('El archivo seleccionado no es una imagen.')
  }

  let result = file
  if (isCompressibleImage(file) && file.size > maxSizeMB * MB) {
    try {
      result = await imageCompression(file, {
        maxSizeMB,
        maxWidthOrHeight,
        useWebWorker: true,
      })
    } catch {
      throw new Error('No se pudo procesar la imagen. Prueba con otra.')
    }
  }

  if (result.size > hardMaxBytes) {
    throw new Error(
      `La imagen es demasiado pesada (${formatBytes(result.size)}). ` +
        `Usa una de menos de ${formatBytes(hardMaxBytes)}.`,
    )
  }

  return result
}
